// src/modules/aiml/ONNXSection.jsx
// ONNX Runtime Web — Load any .onnx model · Inspect · Run · Benchmark · Concepts
// Tabs: overview | load | inspect | run | benchmark | concepts

import React, { useState, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line,
} from 'recharts';
import { ModelLoader, InferenceTag } from './AIMLModule.jsx';

const ACCENT = '#a78bfa';
import { _livePalette as P } from '../../context/paletteSync';
const TABS = ['overview','load','inspect','run','benchmark','concepts'];

const Btn = ({ onClick, children, color = ACCENT, disabled, style }) => (
  <button onClick={onClick} disabled={disabled} style={{ padding: '8px 18px', background: color + '18', border: `1px solid ${color}`, color, fontSize: '10px', cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700, opacity: disabled ? 0.4 : 1, ...style }}>{children}</button>
);
const Tag = ({ children, color = ACCENT }) => (
  <span style={{ padding: '2px 8px', background: color + '20', border: `1px solid ${color}44`, color, fontSize: '9px', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{children}</span>
);
const Panel = ({ title, accent = ACCENT, children, style }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--b0)', borderRadius: '4px', overflow: 'hidden', ...style }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--b0)', fontSize: '9px', letterSpacing: '.1em', color: accent, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>{title}</div>
    <div style={{ padding: '14px' }}>{children}</div>
  </div>
);

// ── ORT CDN loader ────────────────────────────────────────────────
const ORT_CDN = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.20.1/dist/ort.min.js';
let _ortLoaded = false;

async function ensureORT() {
  if (_ortLoaded && window.ort) return;
  await new Promise((res, rej) => {
    if (window.ort) { _ortLoaded = true; res(); return; }
    const s = document.createElement('script');
    s.src = ORT_CDN; s.onload = () => { _ortLoaded = true; res(); }; s.onerror = rej;
    document.head.appendChild(s);
  });
  // Set WASM path for ORT
  window.ort.env.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.20.1/dist/';
}

// ── Pre-baked demo ONNX models (tiny, well-known) ─────────────────
const DEMO_MODELS = [
  {
    id: 'squeezenet',
    name: 'SqueezeNet 1.1',
    url: 'https://media.githubusercontent.com/media/onnx/models/main/validated/vision/classification/squeezenet/model/squeezenet1.1-7.onnx',
    size: '~5MB',
    desc: 'Image classification — 1000 ImageNet classes. Input: float32[1,3,224,224]',
    inputName: 'data', inputShape: [1, 3, 224, 224], outputName: 'squeezenet0_flatten0_reshape0',
  },
  {
    id: 'emotion',
    name: 'Emotion FERPlus',
    url: 'https://media.githubusercontent.com/media/onnx/models/main/validated/vision/body_analysis/emotion_ferplus/model/emotion-ferplus-7.onnx',
    size: '~34MB',
    desc: 'Face emotion recognition — 8 emotions. Input: float32[1,1,64,64]',
    inputName: 'Input3', inputShape: [1, 1, 64, 64], outputName: 'Plus692_Output_0',
  },
  {
    id: 'mnist',
    name: 'MNIST Classifier',
    url: 'https://media.githubusercontent.com/media/onnx/models/main/validated/vision/classification/mnist/model/mnist-7.onnx',
    size: '~26KB',
    desc: 'Handwritten digit recognition — 10 classes. Input: float32[1,1,28,28]',
    inputName: 'Input3', inputShape: [1, 1, 28, 28], outputName: 'Plus214_Output_0',
  },
];

// ── Random tensor generator ───────────────────────────────────────
function makeRandomTensor(ort, shape, type = 'float32') {
  const size = shape.reduce((a, b) => a * b, 1);
  let data;
  if (type === 'float32') data = new Float32Array(size).map(() => Math.random());
  else if (type === 'int64') data = new BigInt64Array(size).fill(0n);
  else data = new Float32Array(size).map(() => Math.random());
  return new ort.Tensor(type, data, shape);
}

// ── Softmax helper ───────────────────────────────────────────────
function softmax(arr) {
  const max = Math.max(...arr);
  const exps = arr.map(v => Math.exp(v - max));
  const sum  = exps.reduce((a, b) => a + b, 0);
  return exps.map(v => v / sum);
}

// Top-k from flat array
function topK(arr, k = 5) {
  return Array.from(arr)
    .map((v, i) => ({ idx: i, val: v }))
    .sort((a, b) => b.val - a.val)
    .slice(0, k);
}

// ═════════════════════════════════════════════════════════════════
export default function ONNXSection({ gpuInfo }) {
  const [tab, setTab] = useState('overview');

  // Load tab
  const [ortReady, setOrtReady]     = useState(false);
  const [ortLoading, setOrtLoading] = useState(false);
  const [session, setSession]       = useState(null);
  const [sessionMeta, setSessionMeta] = useState(null);
  const [loadMode, setLoadMode]     = useState('demo'); // demo | upload
  const [selectedDemo, setSelectedDemo] = useState(DEMO_MODELS[0]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loadStatus, setLoadStatus] = useState('idle');
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadError, setLoadError]   = useState('');

  // Run tab
  const [runResult, setRunResult]   = useState(null);
  const [runTime, setRunTime]       = useState(null);
  const [inputValues, setInputValues] = useState('');

  // Benchmark tab
  const [benchRuns, setBenchRuns]   = useState(20);
  const [benchRunning, setBenchRunning] = useState(false);
  const [benchResults, setBenchResults] = useState(null);
  const [benchHistory, setBenchHistory] = useState([]);

  const sessionRef = useRef(null);

  // ── Load ORT ─────────────────────────────────────────────────
  const initORT = async () => {
    setOrtLoading(true);
    try {
      await ensureORT();
      setOrtReady(true);
    } catch (e) {
      console.error('ORT load error:', e);
    }
    setOrtLoading(false);
  };

  // ── Load ONNX model ──────────────────────────────────────────
  const loadModel = async () => {
    if (!ortReady) { await initORT(); }
    setLoadStatus('loading'); setLoadError(''); setLoadProgress(10);
    try {
      const ort = window.ort;
      let modelData;
      if (loadMode === 'demo') {
        setLoadProgress(30);
        const resp = await fetch(selectedDemo.url);
        if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
        setLoadProgress(70);
        const buf = await resp.arrayBuffer();
        modelData = new Uint8Array(buf);
      } else if (uploadedFile) {
        const buf = await uploadedFile.arrayBuffer();
        modelData = new Uint8Array(buf);
      } else {
        throw new Error('No model selected');
      }
      setLoadProgress(85);
      const sess = await ort.InferenceSession.create(modelData, {
        executionProviders: ['wasm'],
      });
      setLoadProgress(100);
      sessionRef.current = sess;
      setSession(sess);

      // Extract metadata
      const meta = {
        inputs:  sess.inputNames.map(name => ({ name, type: 'float32', shape: 'see inspect' })),
        outputs: sess.outputNames.map(name => ({ name })),
        inputNames:  sess.inputNames,
        outputNames: sess.outputNames,
      };
      setSessionMeta(meta);
      setLoadStatus('ready');
      // Default input values
      const demoModel = DEMO_MODELS.find(m => m.id === selectedDemo?.id);
      if (demoModel) {
        const size = demoModel.inputShape.reduce((a, b) => a * b, 1);
        setInputValues(Array.from({ length: Math.min(size, 12) }, () => (Math.random()).toFixed(3)).join(', ') + (size > 12 ? ` ... (${size} total)` : ''));
      }
    } catch (e) {
      console.error('Model load error:', e);
      setLoadStatus('error'); setLoadError(e.message);
    }
  };

  // ── Run inference ────────────────────────────────────────────
  const runInference = async () => {
    const sess = sessionRef.current;
    if (!sess) return;
    const ort = window.ort;
    try {
      const demoModel = DEMO_MODELS.find(m => m.id === selectedDemo?.id);
      const shape  = demoModel?.inputShape || [1, 3, 224, 224];
      const inName = demoModel?.inputName  || sess.inputNames[0];
      const tensor = makeRandomTensor(ort, shape, 'float32');
      const feeds  = { [inName]: tensor };
      const t0 = performance.now();
      const results = await sess.run(feeds);
      const ms = Math.round(performance.now() - t0);
      setRunTime(ms);
      // Format output
      const outName = sess.outputNames[0];
      const outData = Array.from(results[outName].data);
      const probs   = softmax(outData);
      const top5    = topK(probs, 5);
      setRunResult({ raw: outData.slice(0, 10), probs, top5, outName, shape: results[outName].dims });
    } catch (e) {
      console.error('Run error:', e);
      setRunResult({ error: e.message });
    }
  };

  // ── Benchmark ─────────────────────────────────────────────────
  const runBenchmark = async () => {
    const sess = sessionRef.current;
    if (!sess) return;
    const ort = window.ort;
    setBenchRunning(true);
    const demoModel = DEMO_MODELS.find(m => m.id === selectedDemo?.id);
    const shape  = demoModel?.inputShape || [1, 3, 224, 224];
    const inName = demoModel?.inputName  || sess.inputNames[0];
    const latencies = [];
    for (let i = 0; i < benchRuns; i++) {
      const tensor = makeRandomTensor(ort, shape);
      const t0 = performance.now();
      await sess.run({ [inName]: tensor });
      latencies.push(Math.round(performance.now() - t0));
    }
    const sorted = [...latencies].sort((a, b) => a - b);
    const p50  = sorted[Math.floor(sorted.length * 0.5)];
    const p95  = sorted[Math.floor(sorted.length * 0.95)];
    const p99  = sorted[Math.floor(sorted.length * 0.99)] || sorted[sorted.length - 1];
    const mean = Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length);
    setBenchResults({ latencies, p50, p95, p99, mean, throughput: Math.round(1000 / mean) });
    setBenchHistory(latencies.map((ms, i) => ({ run: i + 1, ms })));
    setBenchRunning(false);
  };

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', gap: '3px', background: 'var(--deep)', padding: '4px', borderRadius: '3px', border: '1px solid var(--b0)', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '6px 14px', border: `1px solid ${tab === t ? ACCENT : 'transparent'}`, background: tab === t ? ACCENT + '18' : 'transparent', color: tab === t ? ACCENT : 'var(--t3)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap', fontWeight: tab === t ? 700 : 400 }}>{t.toUpperCase()}</button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="ONNX RUNTIME WEB — OVERVIEW" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.85 }}>
                <strong style={{ color: ACCENT }}>ONNX Runtime Web</strong> lets you run <em>any</em> ONNX model in the browser — models exported from PyTorch, TensorFlow, scikit-learn, HuggingFace, CoreML, and more. The same model file runs everywhere: Python, mobile, browser.<br /><br />
                ONNX (Open Neural Network Exchange) is a standardized graph format. The runtime compiles the graph to WebAssembly or WebGPU at load time. ORT Web v1.20+ supports 150+ operators and both WASM and experimental WebGPU execution providers.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {[
                  ['Version', '1.20.1', ACCENT],
                  ['Execution providers', 'WASM · WebGPU (beta)', 'var(--cyan)'],
                  ['Supported ops', '150+ ONNX operators', 'var(--green)'],
                  ['Input formats', 'Uint8Array / ArrayBuffer / URL', 'var(--gold)'],
                  ['Quantization', 'INT8 · FP16 · FP32', 'var(--violet)'],
                  ['Export sources', 'PyTorch · TF · sklearn · HF', 'var(--orange)'],
                ].map(([k, v, c]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--raised)', borderRadius: '3px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--t3)' }}>{k}</span>
                    <span style={{ fontSize: '11px', color: c, fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          <Panel title="THE ONNX ECOSYSTEM" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', marginBottom: '14px' }}>
              {[
                { stage: 'TRAIN', items: ['PyTorch', 'TensorFlow/Keras', 'scikit-learn', 'XGBoost', 'HuggingFace'], color: 'var(--green)' },
                { stage: 'EXPORT → .onnx', items: ['torch.onnx.export()', 'tf2onnx', 'sklearn-onnx', 'optimum CLI', 'coremltools'], color: ACCENT },
                { stage: 'DEPLOY', items: ['ORT Web (browser)', 'ORT Mobile (iOS/Android)', 'ORT Server (Python/C#)', 'ORT Core (C++)', 'ONNX.js (legacy)'], color: 'var(--cyan)' },
              ].map(({ stage, items, color }) => (
                <div key={stage} style={{ padding: '12px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '4px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color, marginBottom: '8px', fontFamily: 'JetBrains Mono' }}>{stage}</div>
                  {items.map(item => <div key={item} style={{ fontSize: '10px', color: 'var(--t3)', padding: '3px 0', borderBottom: '1px solid var(--b0)22' }}>· {item}</div>)}
                </div>
              ))}
            </div>
            <pre style={{ margin: 0, padding: '12px', background: 'var(--deep)', borderRadius: '3px', fontSize: '10px', color: 'var(--t2)', fontFamily: 'JetBrains Mono', lineHeight: 1.65, overflowX: 'auto' }}>
{`// Export PyTorch model to ONNX
import torch
model = MyModel(); model.eval()
dummy = torch.randn(1, 3, 224, 224)
torch.onnx.export(model, dummy, "model.onnx",
  input_names=["input"], output_names=["output"],
  dynamic_axes={"input": {0: "batch_size"}})

// Run in browser with ORT Web
import * as ort from 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.20.1/dist/ort.min.js';
ort.env.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.20.1/dist/';
const session = await ort.InferenceSession.create('./model.onnx');
const tensor = new ort.Tensor('float32', data, [1, 3, 224, 224]);
const output = await session.run({ input: tensor });`}
            </pre>
          </Panel>
        </div>
      )}

      {/* ── LOAD ── */}
      {tab === 'load' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="LOAD ONNX MODEL" accent={ACCENT}>
            {!ortReady && (
              <div style={{ marginBottom: '14px' }}>
                <Btn onClick={initORT} disabled={ortLoading} color={ACCENT}>
                  {ortLoading ? 'LOADING ORT…' : 'INIT ORT RUNTIME FIRST →'}
                </Btn>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
              {['demo', 'upload'].map(m => (
                <button key={m} onClick={() => setLoadMode(m)} style={{ padding: '7px 16px', border: `1px solid ${loadMode === m ? ACCENT : 'var(--b0)'}`, background: loadMode === m ? ACCENT + '18' : 'var(--raised)', color: loadMode === m ? ACCENT : 'var(--t3)', fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: loadMode === m ? 700 : 400 }}>
                  {m === 'demo' ? 'DEMO MODELS' : 'UPLOAD .ONNX'}
                </button>
              ))}
            </div>

            {loadMode === 'demo' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '14px' }}>
                {DEMO_MODELS.map(model => (
                  <button key={model.id} onClick={() => setSelectedDemo(model)} style={{ padding: '12px', border: `1px solid ${selectedDemo?.id === model.id ? ACCENT : 'var(--b0)'}`, background: selectedDemo?.id === model.id ? ACCENT + '15' : 'var(--raised)', color: selectedDemo?.id === model.id ? ACCENT : 'var(--t2)', cursor: 'pointer', borderRadius: '4px', textAlign: 'left' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, fontFamily: 'JetBrains Mono', marginBottom: '4px' }}>{model.name}</div>
                    <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '5px' }}>{model.desc}</div>
                    <Tag color={selectedDemo?.id === model.id ? ACCENT : 'var(--t3)'}>{model.size}</Tag>
                  </button>
                ))}
              </div>
            )}

            {loadMode === 'upload' && (
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'inline-block', padding: '10px 20px', background: ACCENT + '18', border: `1px solid ${ACCENT}`, color: ACCENT, fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
                  📁 CHOOSE .ONNX FILE
                  <input type="file" accept=".onnx" onChange={e => { const f = e.target.files[0]; if (f) { setUploadedFile(f); setSelectedDemo(null); } }} style={{ display: 'none' }} />
                </label>
                {uploadedFile && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--green)', fontFamily: 'JetBrains Mono' }}>
                    ✓ {uploadedFile.name} ({(uploadedFile.size / 1024 / 1024).toFixed(1)}MB)
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Btn onClick={loadModel} disabled={!ortReady || loadStatus === 'loading'} color={ACCENT}>
                {loadStatus === 'loading' ? 'LOADING MODEL…' : 'LOAD MODEL →'}
              </Btn>
              {loadStatus === 'ready'   && <Tag color="var(--green)">✓ Session ready</Tag>}
              {loadStatus === 'error'   && <span style={{ fontSize: '11px', color: 'var(--red)', fontFamily: 'JetBrains Mono' }}>✗ {loadError}</span>}
            </div>

            {loadStatus === 'loading' && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ height: '5px', background: 'var(--deep)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${loadProgress}%`, background: ACCENT, borderRadius: '3px', transition: 'width 0.3s' }} />
                </div>
                <div style={{ fontSize: '10px', color: 'var(--t3)', marginTop: '5px' }}>{loadProgress}% — downloading + compiling to WASM…</div>
              </div>
            )}
          </Panel>

          {sessionMeta && (
            <Panel title="SESSION INFO" accent={ACCENT}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>INPUTS ({sessionMeta.inputNames.length})</div>
                  {sessionMeta.inputNames.map(name => (
                    <div key={name} style={{ padding: '8px 10px', background: 'var(--raised)', borderRadius: '3px', marginBottom: '4px' }}>
                      <div style={{ fontSize: '11px', fontFamily: 'JetBrains Mono', color: ACCENT, fontWeight: 700 }}>{name}</div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)' }}>float32 tensor</div>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>OUTPUTS ({sessionMeta.outputNames.length})</div>
                  {sessionMeta.outputNames.map(name => (
                    <div key={name} style={{ padding: '8px 10px', background: 'var(--raised)', borderRadius: '3px', marginBottom: '4px' }}>
                      <div style={{ fontSize: '11px', fontFamily: 'JetBrains Mono', color: 'var(--green)', fontWeight: 700 }}>{name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* ── INSPECT ── */}
      {tab === 'inspect' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {!session ? (
            <Panel title="LOAD A MODEL FIRST" accent={ACCENT}>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: 'var(--t3)', marginBottom: '14px' }}>Load a model in the LOAD tab to inspect its graph structure.</div>
                <Btn onClick={() => setTab('load')} color={ACCENT}>GO TO LOAD TAB →</Btn>
              </div>
            </Panel>
          ) : (
            <>
              <Panel title="MODEL GRAPH INSPECTION" accent={ACCENT}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>INPUT TENSORS</div>
                    {session.inputNames.map((name, i) => {
                      const demo = DEMO_MODELS.find(m => m.id === selectedDemo?.id);
                      return (
                        <div key={name} style={{ padding: '10px 12px', background: 'var(--raised)', borderRadius: '4px', marginBottom: '6px', border: `1px solid ${ACCENT}33` }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: ACCENT, fontFamily: 'JetBrains Mono', marginBottom: '5px' }}>{name}</div>
                          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                            <Tag>float32</Tag>
                            {demo && <Tag color="var(--cyan)">[{demo.inputShape.join(', ')}]</Tag>}
                            {demo && <Tag color="var(--green)">{demo.inputShape.reduce((a,b)=>a*b,1)} elements</Tag>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div>
                    <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>OUTPUT TENSORS</div>
                    {session.outputNames.map(name => (
                      <div key={name} style={{ padding: '10px 12px', background: 'var(--raised)', borderRadius: '4px', marginBottom: '6px', border: '1px solid var(--green)33' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--green)', fontFamily: 'JetBrains Mono' }}>{name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Panel>

              <Panel title="SELECTED MODEL DETAILS" accent={ACCENT}>
                {(() => {
                  const demo = DEMO_MODELS.find(m => m.id === selectedDemo?.id);
                  if (!demo) return <div style={{ fontSize: '11px', color: 'var(--t3)' }}>Custom uploaded model — inspect via Netron for full graph visualization.</div>;
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75 }}>
                        <strong style={{ color: ACCENT }}>{demo.name}</strong><br />{demo.desc}<br /><br />
                        Input shape: <code style={{ color: ACCENT }}>[{demo.inputShape.join(', ')}]</code> = {demo.inputShape.reduce((a,b)=>a*b,1).toLocaleString()} floats<br />
                        File size: {demo.size}
                      </div>
                      <div style={{ padding: '12px', background: 'var(--deep)', borderRadius: '4px' }}>
                        <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '6px', letterSpacing: '.1em' }}>MEMORY ESTIMATE</div>
                        {[
                          ['Input tensor', `${(demo.inputShape.reduce((a,b)=>a*b,1) * 4 / 1024).toFixed(0)} KB`],
                          ['Output tensor', demo.id === 'squeezenet' ? '4 KB (1000 floats)' : demo.id === 'mnist' ? '0.04 KB (10 floats)' : '0.1 KB'],
                          ['WASM heap', '~50MB reserved'],
                        ].map(([k, v]) => (
                          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--b0)33' }}>
                            <span style={{ fontSize: '10px', color: 'var(--t3)' }}>{k}</span>
                            <span style={{ fontSize: '10px', color: ACCENT, fontFamily: 'JetBrains Mono' }}>{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                <div style={{ marginTop: '12px', padding: '10px', background: 'var(--raised)', borderRadius: '3px', fontSize: '10px', color: 'var(--t3)', lineHeight: 1.65 }}>
                  💡 For full graph visualization (layers, ops, connections), open your .onnx file at <a href="https://netron.app" target="_blank" rel="noreferrer" style={{ color: ACCENT }}>netron.app</a> — works entirely in-browser.
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── RUN ── */}
      {tab === 'run' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {!session ? (
            <Panel title="LOAD A MODEL FIRST" accent={ACCENT}>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <Btn onClick={() => setTab('load')} color={ACCENT}>GO TO LOAD TAB →</Btn>
              </div>
            </Panel>
          ) : (
            <>
              <Panel title="RUN INFERENCE" accent={ACCENT}>
                <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '14px', lineHeight: 1.7 }}>
                  Runs inference with a <strong style={{ color: ACCENT }}>random input tensor</strong> of the correct shape. Real input (image pixel data) would give meaningful class predictions — random input demonstrates the inference pipeline.
                </div>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Btn onClick={runInference} color={ACCENT}>RUN INFERENCE →</Btn>
                  {runTime && <InferenceTag ms={runTime} />}
                  {session && <Tag color="var(--t3)">{session.inputNames[0]} → {session.outputNames[0]}</Tag>}
                </div>

                {runResult?.error && (
                  <div style={{ padding: '12px', background: 'var(--red)18', borderRadius: '3px', fontSize: '11px', color: 'var(--red)', fontFamily: 'JetBrains Mono' }}>{runResult.error}</div>
                )}

                {runResult && !runResult.error && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>
                        RAW OUTPUT ({runResult.shape?.join('×')} tensor, first 10 values)
                      </div>
                      <div style={{ padding: '12px', background: 'var(--deep)', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'var(--t2)', lineHeight: 1.7 }}>
                        [{runResult.raw.map(v => v.toFixed(4)).join(', ')}{runResult.raw.length < (runResult.probs?.length || 0) ? ', …' : ''}]
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>TOP-5 CLASS PROBABILITIES (after softmax)</div>
                      {runResult.top5?.map(({ idx, val }, i) => (
                        <div key={idx} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--b0)33' }}>
                          <span style={{ fontSize: '10px', color: 'var(--t3)', fontFamily: 'JetBrains Mono', minWidth: '50px' }}>class {idx}</span>
                          <div style={{ flex: 1, height: '5px', background: 'var(--deep)', borderRadius: '2px' }}>
                            <div style={{ height: '100%', width: `${val * 100}%`, background: P[i], borderRadius: '2px' }} />
                          </div>
                          <span style={{ fontSize: '10px', color: P[i], fontFamily: 'JetBrains Mono', minWidth: '46px', textAlign: 'right', fontWeight: 700 }}>{(val * 100).toFixed(2)}%</span>
                        </div>
                      ))}
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginTop: '8px' }}>
                        Random input → ~uniform distribution (each class ~{(100 / (runResult.probs?.length || 1000)).toFixed(3)}%)
                      </div>
                    </div>
                  </div>
                )}
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── BENCHMARK ── */}
      {tab === 'benchmark' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {!session ? (
            <Panel title="LOAD A MODEL FIRST" accent={ACCENT}>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <Btn onClick={() => setTab('load')} color={ACCENT}>GO TO LOAD TAB →</Btn>
              </div>
            </Panel>
          ) : (
            <>
              <Panel title="INFERENCE LATENCY BENCHMARK" accent={ACCENT}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '5px' }}>Runs: {benchRuns}</div>
                    <input type="range" min={5} max={100} step={5} value={benchRuns} onChange={e => setBenchRuns(+e.target.value)} style={{ width: '140px', accentColor: ACCENT }} />
                  </div>
                  <Btn onClick={runBenchmark} disabled={benchRunning} color={ACCENT}>
                    {benchRunning ? `RUNNING ${benchRuns} INFERENCES…` : `BENCHMARK ${benchRuns} RUNS →`}
                  </Btn>
                </div>

                {benchResults && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '8px', marginBottom: '14px' }}>
                      {[
                        ['p50', `${benchResults.p50}ms`, ACCENT],
                        ['p95', `${benchResults.p95}ms`, 'var(--gold)'],
                        ['p99', `${benchResults.p99}ms`, 'var(--orange)'],
                        ['mean', `${benchResults.mean}ms`, 'var(--cyan)'],
                        ['throughput', `${benchResults.throughput}/s`, 'var(--green)'],
                      ].map(([k, v, c]) => (
                        <div key={k} style={{ padding: '10px', background: 'var(--raised)', border: `1px solid ${c}33`, borderRadius: '4px', textAlign: 'center' }}>
                          <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '4px' }}>{k}</div>
                          <div style={{ fontSize: '18px', fontWeight: 700, fontFamily: 'Syne', color: c }}>{v}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>LATENCY PER RUN (ms)</div>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={benchHistory}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                        <XAxis dataKey="run" stroke="var(--t3)" tick={{ fontSize: 8 }} />
                        <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} />
                        <Tooltip formatter={v => [`${v}ms`, 'latency']} />
                        <Line type="monotone" dataKey="ms" stroke={ACCENT} strokeWidth={2} dot={false} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab === 'concepts' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {[
            { title:'ONNX Graph Format', color:ACCENT, desc:'ONNX models are stored as Protocol Buffer files (.onnx). The graph contains: nodes (ops like Conv, Relu, MatMul), tensors (weights stored as initializers), input/output specs with shape and dtype. ORT parses this graph and builds an execution plan — fusing ops, memory planning, selecting kernels.' },
            { title:'WASM Execution Provider', color:'var(--cyan)', desc:'The default ORT Web backend. Compiles a custom C++ kernel library to WebAssembly with SIMD extensions. XNNPACK (Google\'s optimized op library) provides kernels for common ops. WASM SIMD gives 4× throughput for float32 matrix ops. Thread support (SharedArrayBuffer) allows multi-core parallelism.' },
            { title:'WebGPU Execution Provider', color:'var(--violet)', desc:'Experimental in ORT 1.20. Compiles ONNX ops to WebGPU compute shaders (WGSL). Matrix multiplications run in parallel across GPU cores. 10-100× faster than WASM for large models. Currently supports a subset of ops — falls back to WASM for unsupported. Enable via executionProviders: ["webgpu"].' },
            { title:'INT8 Quantization', color:'var(--gold)', desc:'Post-training quantization maps float32 weights to int8 (scale + zero_point). ~4× smaller, 2-3× faster on CPU with VNNI instructions, minimal accuracy loss (<1% on most models). ORT supports dynamic quantization (weights only) and static quantization (weights + activations). INT8 is the recommended format for browser deployment.' },
            { title:'Exporting from PyTorch', color:'var(--green)', desc:'torch.onnx.export(model, dummy_input, "model.onnx", opset_version=17). Opset version matters: newer opsets have more ops. Use dynamic_axes for variable batch sizes. Check with onnxruntime.InferenceSession locally before deploying. For HuggingFace models use optimum: optimum-cli export onnx --model bert-base-uncased ./out.' },
            { title:'Netron — Graph Visualizer', color:'var(--orange)', desc:'Netron (netron.app) renders the full ONNX computation graph interactively: every node, edge, tensor shape, and attribute. Works 100% in-browser — no upload. Essential for debugging model issues, understanding architecture, verifying export correctness, and identifying bottleneck ops. Supports ONNX, TFLite, CoreML, PyTorch, and more.' },
          ].map(({ title, color, desc }) => (
            <div key={title} style={{ padding: '16px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '4px' }}>
              <div style={{ color, fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>{title}</div>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75 }}>{desc}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
